import Link from 'components/Basic/Link/Link';
import Button from 'components/Forms/Button/Button';
import Form from 'components/Forms/Form/Form';
import FormColumn from 'components/Forms/Lib/FormColumn/FormColumn';
import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import Textarea from 'components/Forms/Textarea/Textarea';
import TextInput from 'components/Forms/TextInput/TextInput';
import { showSuccessMessage } from 'components/Helpers/Toasts';
import { Webline } from 'components/Layout/Webline/Webline';
import { ContactTextStyled, ContactWrapper } from 'components/Pages/Contact/Contact.style';
import { useContactForm, useContactFormMeta } from 'components/Pages/Contact/formMeta';
import { useSettings } from 'connectors/Settings/Settings';
import { useContactMutationApi } from 'graphql/generated';
import { clearForm } from 'helpers/forms/clearForm';
import { handleFormErrors } from 'helpers/forms/handleFormErrors';
import { useErrorPopup } from 'hooks/forms/useErrorPopup';
import { useGetPrivacyPolicyUrl } from 'hooks/routes/useGetPrivacyPolicyUrl';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { ContactFormType } from 'types/form';
import { GtmMessageOriginType } from 'types/gtm';

const Contact: FC = () => {
    const { t } = useTranslation();
    const [formProviderMethods, defaultValues] = useContactForm();
    const formMeta = useContactFormMeta(formProviderMethods);

    const settings = useSettings();

    const gdprUrl = useGetPrivacyPolicyUrl();

    const [, contactMutation] = useContactMutationApi();

    useErrorPopup(formProviderMethods, formMeta.fields, undefined, GtmMessageOriginType.other);

    const onSubmitHandler: SubmitHandler<ContactFormType> = async (values) => {
        const contactResult = await contactMutation({ input: values });

        if (contactResult.data?.Contact !== undefined) {
            showSuccessMessage(formMeta.messages.success);
        }

        handleFormErrors(contactResult.error, formProviderMethods, t, formMeta.messages.error, undefined);
        clearForm(contactResult.error, formProviderMethods, defaultValues);
    };

    return (
        <>
            <ContactWrapper>
                <Webline>
                    <h1 data-testid="basic-heading-h1">{t('Write to us')}</h1>
                    {settings?.contactFormMainText !== undefined && (
                        <ContactTextStyled dangerouslySetInnerHTML={{ __html: settings.contactFormMainText }} />
                    )}
                    <FormProvider {...formProviderMethods}>
                        <Form onSubmit={formProviderMethods.handleSubmit(onSubmitHandler)}>
                            <FormColumn className="lg:w-[calc(65%+0.75rem)]">
                                <FormLine bottomGap className="w-full flex-none lg:w-1/2">
                                    <Controller
                                        name={formMeta.fields.name.name}
                                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                                            <>
                                                <TextInput
                                                    required
                                                    fieldRef={field}
                                                    hasError={invalid}
                                                    id={formMeta.formName + '-' + formMeta.fields.name.name}
                                                    isTouched={isTouched}
                                                    label={formMeta.fields.name.label}
                                                    name={formMeta.fields.name.name}
                                                    type="text"
                                                />
                                                <FormLineError
                                                    error={error}
                                                    inputType="text-input"
                                                    data-testid={
                                                        formMeta.formName + '-' + formMeta.fields.name.name + '-error'
                                                    }
                                                />
                                            </>
                                        )}
                                    />
                                </FormLine>
                            </FormColumn>
                            <FormColumn className="lg:w-[calc(65%+0.75rem)]">
                                <FormLine bottomGap className="w-full flex-none lg:w-1/2">
                                    <Controller
                                        name={formMeta.fields.email.name}
                                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                                            <>
                                                <TextInput
                                                    required
                                                    fieldRef={field}
                                                    hasError={invalid}
                                                    id={formMeta.formName + '-' + formMeta.fields.email.name}
                                                    isTouched={isTouched}
                                                    label={formMeta.fields.email.label}
                                                    name={formMeta.fields.email.name}
                                                    type="email"
                                                />
                                                <FormLineError
                                                    className="after:bg-transparent"
                                                    error={error}
                                                    inputType="text-input"
                                                    data-testid={
                                                        formMeta.formName + '-' + formMeta.fields.email.name + '-error'
                                                    }
                                                />
                                            </>
                                        )}
                                    />
                                </FormLine>
                            </FormColumn>
                            <FormColumn className="lg:w-[calc(65%+0.75rem)]">
                                <FormLine bottomGap className="w-full flex-none ">
                                    <Controller
                                        name={formMeta.fields.message.name}
                                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                                            <>
                                                <Textarea
                                                    required
                                                    fieldRef={field}
                                                    hasError={invalid}
                                                    id={formMeta.formName + '-' + formMeta.fields.message.name}
                                                    isTouched={isTouched}
                                                    label={formMeta.fields.message.label}
                                                    name={formMeta.fields.message.name}
                                                    rows={4}
                                                />
                                                <FormLineError
                                                    error={error}
                                                    inputType="textarea"
                                                    data-testid={
                                                        formMeta.formName +
                                                        '-' +
                                                        formMeta.fields.message.name +
                                                        '-error'
                                                    }
                                                />
                                            </>
                                        )}
                                    />
                                </FormLine>
                            </FormColumn>
                            <Controller
                                name={formMeta.fields.subject.name}
                                render={({ field }) => (
                                    <div
                                        aria-hidden="true"
                                        className="absolute -left-[10000px] h-px w-px select-none opacity-0"
                                    >
                                        <label htmlFor={formMeta.formName + '-' + formMeta.fields.subject.name}>
                                            <input
                                                autoComplete="off"
                                                id={formMeta.formName + '-' + formMeta.fields.subject.name}
                                                name={field.name}
                                                ref={field.ref}
                                                tabIndex={-1}
                                                type="text"
                                                value={field.value}
                                                onBlur={field.onBlur}
                                                onChange={field.onChange}
                                            />
                                            {formMeta.fields.subject.label}
                                        </label>
                                    </div>
                                )}
                            />
                            <ContactTextStyled>
                                <Trans
                                    defaultTrans="By clicking on the Send message button, you agree with the <lnk1>processing of privacy policy</lnk1>."
                                    i18nKey="ContactFormInfo"
                                    components={{
                                        lnk1: <Link href={gdprUrl} linkType="external" target="_blank" />,
                                    }}
                                />
                            </ContactTextStyled>
                            <Button
                                hasDisabledLook={!formProviderMethods.formState.isValid}
                                type="submit"
                                variant="primary"
                            >
                                {t('Send message')}
                            </Button>
                        </Form>
                    </FormProvider>
                </Webline>
            </ContactWrapper>
        </>
    );
};

/* @component */
export default Contact;
